import { NIGHTMARES } from '@somnhold/content';
import { DIRS } from '../hex.ts';
import { cellAtWorld, cellCenter, heroWalkable } from '../grid.ts';
import { cellQ, cellR, cellId } from '../grid.ts';
import { iatan2, idiv, imax, mulBp, withinRange } from '../math/fixed.ts';
import { rngInt, rngRange } from '../math/rng.ts';
import type { BattleState, Enemy, Hero } from '../types.ts';
import type { Pt } from '../caza-types.ts';
import type { FloesCrack, FloesRules, FloesState } from '../isla-types.ts';
import { damageHero, ev, spawnEnemy } from './combat.ts';
import { stepToward } from './enemies.ts';
import { drown, fallHero, hasCell, hitKept, setOpen } from './isla-ice.ts';

/**
 * The ice that moves (docs/sendas/barbaro.md, chapter 4, act 1: «El hielo que se mueve»; the ground in
 * battle/isla-layout.ts). The White Island's shore with Sigrun's camp of piled stones, three floes off it joined to it by
 * necks of ice that part and join with the water: a neck cracks three seconds before it parts, and what stands on it
 * goes in. Every so often a crack runs through where the hero stands, marked three seconds first: it opens and takes
 * what is on it (the hero comes up a fifth of its life less; a wolf or a colossus, gone: drawn onto it, the ice fights for
 * you). Three bands of the pack and frost colossi come off the floes, on their time and never before the last is down:
 * a colossus goes for the camp past the hero (it strikes it on the way), and one wolf in three gnaws at its stones while the hero isn't by. Won with the third band down; lost with
 * the camp in the water (25 blows) or the hero down.
 *
 * Cost: the necks' cycle read off the clock; one crack at a time; the plugs set only when something opens or shuts.
 */

const fr = (s: BattleState): FloesRules | undefined => s.rules?.senda?.isla?.floes;
const fs = (s: BattleState): FloesState | undefined => s.senda?.isla?.floes;

const fresh = (r: FloesRules): FloesState => ({
  band: 0,
  next: r.first,
  part: 0,
  partAt: -1,
  bandAt: 0,
  necks: r.necks.map(() => 0),
  crack: null,
  crackNext: r.crack.first,
  colCd: [],
  gnawCd: r.gnaw,
  falls: 0,
  drowned: 0,
  parts: 0,
  cracks: 0,
  done: -1,
  lost: -1,
});

/** A neck's stage at this time: 0 joined, 1 cracking, 2 parted. */
export const neckStage = (r: FloesRules, k: number, t: number): number => {
  const cyc = r.join + r.warn + r.apart;
  const p = (t + (r.offsets[k] ?? 0)) % cyc;
  return p < r.join ? 0 : p < r.join + r.warn ? 1 : 2;
};

/** Ticks till a neck's stage next changes. */
export const neckLeft = (r: FloesRules, k: number, t: number): number => {
  const cyc = r.join + r.warn + r.apart;
  const p = (t + (r.offsets[k] ?? 0)) % cyc;
  return p < r.join ? r.join - p : p < r.join + r.warn ? r.join + r.warn - p : cyc - p;
};

/** The open water now: the parted necks and the open crack. */
const openNow = (r: FloesRules, w: FloesState): number[][] => {
  const out: number[][] = [];
  w.necks.forEach((st, k) => {
    if (st === 2) out.push(r.necks[k] as number[]);
  });
  if (w.crack?.open) out.push(w.crack.cells);
  return out;
};

/** Whatever stands on these cells goes into the water. */
const takeOn = (s: BattleState, r: FloesRules, w: FloesState, cells: number[], h: Hero | undefined, cue: (c: string) => void): void => {
  for (const e of s.enemies) {
    if (e.hp <= 0 || NIGHTMARES[e.kind].flying || !hasCell(cells, cellAtWorld(s, e.x, e.y))) continue;
    drown(s, e, h);
    w.drowned++;
    ev(s, { t: 'senda', kind: 'iDrown', npc: '', x: e.x, y: e.y });
    if (w.drowned === 1) cue('drown1');
  }
  if (h && hasCell(cells, cellAtWorld(s, h.x, h.y))) {
    w.falls++;
    fallHero(s, h, r.fallBp, 'iFall');
    cue(`fall${w.falls}`);
  }
};

/** The cells of a crack through a point along a hex direction (`len` of them; only ice, never the camp's). */
const crackCells = (s: BattleState, r: FloesRules, at: number, dir: number): number[] => {
  const R = s.map.radius;
  const q0 = cellQ(R, at);
  const r0 = cellR(R, at);
  const d = DIRS[dir] as { q: number; r: number };
  const half = r.crack.len >> 1;
  const out: number[] = [];
  for (let k = -half; k < r.crack.len - half; k++) {
    const c = cellId(R, q0 + d.q * k, r0 + d.r * k);
    if (c < 0 || hasCell(r.campCells, c) || !heroWalkable(s, c)) continue;
    out.push(c);
  }
  return out.sort((a, b) => a - b);
};

/** A part of a band onto the floes: its share of the wolves (and the colossi, in the first), spread over the three. */
const sendPart = (s: BattleState, r: FloesRules, b: [number, number], part: number): void => {
  const per = idiv(b[0], r.parts);
  const wolves = part === r.parts - 1 ? b[0] - per * (r.parts - 1) : per;
  const at = rngInt(s.rng, r.spawns.length);
  for (let i = 0; i < wolves; i++) {
    const p = r.spawns[(at + i) % r.spawns.length] as Pt;
    spawnEnemy(s, 'wolf', 1, false, 0, p.x + ((i % 3) - 1) * 450, p.y + (i % 2) * 400, r.wolfHp);
  }
  if (part !== 0) return;
  for (let i = 0; i < b[1]; i++) {
    const p = r.spawns[(at + 1 + i) % r.spawns.length] as Pt;
    const e = spawnEnemy(s, 'frostColossus', 1, false, 0, p.x, p.y, r.colossusHp);
    e.damage = imax(1, mulBp(e.damage, r.colossusDmgBp));
  }
};

export const floesTick = (s: BattleState, cue: (c: string) => void): void => {
  const r = fr(s);
  const st = s.senda;
  if (!r || !st) return;
  const is = (st.isla ??= {});
  const w = (is.floes ??= fresh(r));
  if (!s.started || w.done >= 0 || w.lost >= 0) return;
  const h = s.heroes.find((x) => x.downTicks === 0);
  let changed = false;
  // The necks: crack, part (taking what's on them), join.
  r.necks.forEach((cells, k) => {
    const now = neckStage(r, k, s.waveT);
    if (now === w.necks[k]) return;
    w.necks[k] = now;
    const at = r.neckAt[k] as Pt;
    changed = true;
    if (now === 1) ev(s, { t: 'senda', kind: 'iNeckWarn', npc: '', x: at.x, y: at.y, n: k });
    else if (now === 2) {
      w.parts++;
      ev(s, { t: 'senda', kind: 'iNeckPart', npc: '', x: at.x, y: at.y, n: k });
      if (w.parts === 1) cue('part1');
      setOpen(s, openNow(r, w));
      takeOn(s, r, w, cells, h, cue);
    } else ev(s, { t: 'senda', kind: 'iNeckJoin', npc: '', x: at.x, y: at.y, n: k });
  });
  if (changed) setOpen(s, openNow(r, w));
  // The crack under the hero: marked, open, shut.
  const c = w.crack;
  if (c && !c.open && s.tick >= c.opens) {
    c.open = true;
    ev(s, { t: 'senda', kind: 'iCrackOpen', npc: '', x: c.at.x, y: c.at.y, n: c.dir });
    setOpen(s, openNow(r, w));
    takeOn(s, r, w, c.cells, h, cue);
  } else if (c && c.open && s.tick >= c.closes) {
    w.crack = null;
    ev(s, { t: 'senda', kind: 'iCrackShut', npc: '', x: c.at.x, y: c.at.y, n: c.dir });
    setOpen(s, openNow(r, w));
  } else if (!c && h && s.waveT >= w.crackNext) {
    const at = cellAtWorld(s, h.x, h.y);
    const dir = rngInt(s.rng, 6);
    const cells = at >= 0 && !hasCell(r.campCells, at) ? crackCells(s, r, at, dir) : [];
    w.crackNext = s.waveT + rngRange(s.rng, r.crack.every[0], r.crack.every[1]);
    if (cells.length >= 2) {
      const p = cellCenter(s.map.radius, at);
      w.crack = { cells, at: p, dir, opens: s.tick + r.warn, closes: s.tick + r.warn + r.crack.open, open: false } satisfies FloesCrack;
      w.cracks++;
      ev(s, { t: 'senda', kind: 'iCrackWarn', npc: '', x: p.x, y: p.y, n: dir });
      cue(`crack${w.cracks}`);
    }
  }
  // The bands: off the floes in parts; the next on its time, once the last is down.
  const b = r.bands[w.band];
  if (w.next >= 0 && s.waveT >= w.next && b) {
    w.band++;
    w.bandAt = s.waveT;
    w.part = 0;
    w.partAt = s.waveT;
    w.next = -1;
    ev(s, { t: 'senda', kind: 'iBand', npc: '', x: 0, y: 0, n: w.band });
    cue(`band${w.band}`);
    if (b[1] > 0 && w.band === 1) cue('colossus1');
  }
  const cb = r.bands[w.band - 1];
  if (cb && w.partAt >= 0 && s.waveT >= w.partAt) {
    sendPart(s, r, cb, w.part);
    w.part++;
    w.partAt = w.part < r.parts ? s.waveT + r.part : -1;
  } else if (w.next < 0 && w.partAt < 0 && !s.enemies.some((e) => e.hp > 0)) {
    if (w.band >= r.bands.length) {
      w.done = s.tick;
      cue('bandsDone');
      return;
    }
    w.next = imax(s.waveT + r.gap, w.bandAt + r.every);
  }
  // The colossi's blows on the camp, and the gnawers'.
  for (const e of s.enemies) {
    if (e.hp <= 0 || e.kind !== 'frostColossus') continue;
    let cd = w.colCd.find((x) => x[0] === e.id);
    if (!cd) {
      cd = [e.id, 0];
      w.colCd.push(cd);
    }
    if (cd[1] > 0) cd[1]--;
    else if (withinRange(e.x, e.y, r.camp.x, r.camp.y, r.campReach)) {
      cd[1] = NIGHTMARES.frostColossus.reload;
      e.anim = 'attack';
      e.animT = 0;
      if (hitKept(s, 'camp', r.colossusHit, r.camp.x, r.camp.y, 'iCampHit')) w.lost = s.tick;
    }
  }
  w.colCd = w.colCd.filter((x) => s.enemies.some((e) => e.id === x[0] && e.hp > 0));
  if (w.gnawCd > 0) w.gnawCd--;
  else {
    w.gnawCd = r.gnaw;
    let n = 0;
    for (const e of s.enemies) if (e.hp > 0 && gnaws(e) && withinRange(e.x, e.y, r.camp.x, r.camp.y, r.campReach) && !(h && withinRange(h.x, h.y, e.x, e.y, r.gnawReach))) n++;
    if (n > 0 && hitKept(s, 'camp', n, r.camp.x, r.camp.y, 'iCampHit')) w.lost = s.tick;
  }
};

/** One wolf in three goes for the camp's stones. */
const gnaws = (e: Enemy): boolean => e.kind === 'wolf' && e.id % 3 === 0;

/**
 * A colossus walks for the camp over the shore (and a gnawer while the hero isn't by); off the shore, on a floe or a
 * neck, they come as any (the board's own paths, which wait at a parted neck). True: the act moved it.
 */
export const floesHold = (s: BattleState, e: Enemy, px: (e: Enemy) => number): boolean => {
  const r = fr(s);
  if (!r || e.hp <= 0 || e.stun > 0) return false;
  const colossus = e.kind === 'frostColossus';
  if (!colossus && !gnaws(e)) return false;
  if (!hasCell(r.shore, cellAtWorld(s, e.x, e.y))) return false;
  const h = s.heroes.find((x) => x.downTicks === 0);
  // A gnawer with the hero by it fights (as any); a colossus doesn't stop for the hero (it isn't here for it): it strikes
  // it on the way, if it's in its reach.
  if (!colossus && h && withinRange(h.x, h.y, e.x, e.y, r.gnawReach)) return false;
  if (colossus && h && withinRange(h.x, h.y, e.x, e.y, NIGHTMARES.frostColossus.reach)) {
    const w = fs(s);
    const cd = w?.colCd.find((x) => x[0] === e.id);
    if (cd && cd[1] === 0 && !withinRange(e.x, e.y, r.camp.x, r.camp.y, r.campReach)) {
      cd[1] = NIGHTMARES.frostColossus.reload;
      e.anim = 'attack';
      e.animT = 0;
      e.facing = iatan2(h.y - e.y, h.x - e.x);
      damageHero(s, h, e.damage, e, 'frost', false);
      return true;
    }
  }
  if (withinRange(e.x, e.y, r.camp.x, r.camp.y, r.campReach - 200)) {
    e.facing = iatan2(r.camp.y - e.y, r.camp.x - e.x);
    if (e.anim !== 'attack' || e.animT > 10) e.anim = 'idle';
    return true;
  }
  e.anim = 'walk';
  stepToward(e, r.camp.x, r.camp.y, px(e));
  return true;
};

/** The bands down, of three. */
export const floesGoal = (s: BattleState): { done: number; need: number } => {
  const r = fr(s);
  const w = fs(s);
  if (!r || !w) return { done: 0, need: r?.bands.length ?? 3 };
  const alive = s.enemies.some((e) => e.hp > 0);
  const down = w.done >= 0 ? r.bands.length : imaxBand(w.band - (alive || w.partAt >= 0 ? 1 : 0));
  return { done: down, need: r.bands.length };
};

const imaxBand = (n: number): number => (n < 0 ? 0 : n);

export const floesResult = (s: BattleState): 'won' | 'lost' | null => {
  const w = fs(s);
  if (!w) return null;
  if (w.lost >= 0 && s.tick >= w.lost + 24) return 'lost';
  return w.done >= 0 && s.tick >= w.done + 24 ? 'won' : null;
};
